/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  createAgentSession,
  getAgentSession,
  getAgentSessionControls,
  updateAgentSessionStep,
} from "@/modules/sessions/requests";
import {
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionType,
  QARagCreateRequest,
  QaAgentSession
} from '@/modules/sessions/types';
import { AGENT_ROUTES, ROUTES } from "@/shared/constants/routes";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { userStateSelector, useUserStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user.ts";
import { NavigateFunction } from "react-router-dom";
import { emitRagCreate, getRAGExcelFromJSON } from "../requests/index.ts";
import {
  emitQuestionnaireStructure
} from '../requests/questionnaire';
import { getAgentData, getAgentStateActions } from "../states";
import {
  AGENT_TYPES,
  AgentData,
  AgentSourceFile,
  AgentSpecificReviewResponseType,
  QuestionnaireTableRow,
  QuestionnaireTypes,
  ReviewResponseData,
  ReviewResponseType,
  dataNotFound
} from '../types/index.ts';
import {
  ColumnMappingData,
  QAFillerRequest,
  QuestionReviewReponse,
  QuestionnaireFileData,
  QuestionnaireFiller,
  QuestionnaireMapping,
  QuestionnaireStructure,
  SheetStatus
} from '../types/questionnaire.ts';
import { getColumnIndex } from "../utils";
import { useAgentSocket } from "../utils/agent-socket.ts";
import { AutoSaveFunctionArgs } from "../utils/autosave";
import { getFileNameForDownloadReport } from "../utils/downloadReport.ts";
import { getFileContent, getSheetData } from "../utils/excel-parser";
import { getFileNameFromUrl } from "../utils/get-filename-from-url";
import { getRenderType } from "../utils/get-render-type";
import { getResponseTypes } from "../utils/get-response-type";
import { getFieldTitle } from "../utils/getFieldTitle";

interface ProcessFilesForMappingArgs {
  questionnaireFile: {
    file: File;
    url: string;
  };
  sourceFiles: AgentSourceFile[];
  navigate?: NavigateFunction;
  name?: string;
  customer_name?: string;
}

export const processFilesForMapping = async ({
  questionnaireFile,
  sourceFiles,
  name,
  navigate,
  customer_name,
}: ProcessFilesForMappingArgs) => {
  const { file, url } = questionnaireFile;

  const { content } = await getFileContent(file);
  const { sheetNames, workbook } = await getSheetData(content);

  const selectedResponse = userStateSelector.getState().aiResponseType === AiResponseType.NONE ? AiResponseType.LITE : userStateSelector.getState().aiResponseType;
  let selectedSheet = "";
  const fileDetails: QuestionnaireFileData = {
    url,
    fileName: file?.name ?? getFileNameFromUrl(url),
    workbook,
    sheetNames,
    sheetData: {},
  };

  for (let i = 0; i < sheetNames.length; i++) {
    if (!sheetNames[i].endsWith("::hidden")) {
      selectedSheet = sheetNames[i];
      break;
    }
  }

  const qa_structure: QAFillerRequest = {
    excel_url: url,
    is_light: useUserStateSelector.getState().aiResponseType === AiResponseType.LITE,
    source_urls: sourceFiles.map((f) => f.url),
    kb_url: sourceFiles[0].url,
    customer_name,
  };

  const {
    data: { session, steps },
  } = await createAgentSession({
    name: name ?? "Security Questionnaire",
    type: AgentSessionType.QA_FILLER,
    [AgentSessionType.QA_FILLER]: qa_structure,
  });

  if (!session || !session.id) {
    throw new Error("An error occurred");
  }

  const stepData = steps.reduce((acc, step) => {
    if (step.type === AgentSessionStepType.QA_EXCEL_STRUCTURE_MAPPING) {
      step.status = AgentSessionStatus.IN_PROGRESS;
    }
    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

  const { response_mode, response_quality } =
    getResponseTypes(selectedResponse);

  const agentData: AgentData<AGENT_TYPES.QUESTIONNAIRE, QuestionnaireTypes.QA> =
  {
    agentType: AGENT_TYPES.QUESTIONNAIRE,
    subType: QuestionnaireTypes.QA,
    sessionData: session as QaAgentSession,
    stepData,
    responseQuality: response_quality,
    mainData: {
      sourceFilesUrls: new Map(
        sourceFiles.map((file) => [file.fileName, file]),
      ),
      questionnaireFileData: fileDetails,
      selectedSheet,
      approvedIds: [],
      editedIds: [],
      questionnaireMapping: {},
      questionnaireFiller: [],
    },
  };

  const { setAgentData, setSelectedSheet } = getAgentStateActions();
  setAgentData(session.id, agentData);
  if(selectedSheet){
    await setSelectedSheet(session.id, selectedSheet);
  }
  const agent_session_step_id =
    steps.find(
      (step) => step.type === AgentSessionStepType.QA_EXCEL_STRUCTURE_MAPPING,
    )?.id ?? "";

  await emitQuestionnaireStructure(
    {
      agent_session_id: session.id,
      agent_session_step_id,
      qa_structure,
      response_mode,
      response_quality,
    },
    (response: any) => {
      if (response.status === false) {
        if (
          !!response?.message &&
          response?.message.length > 0 &&
          response.message === "Token expired"
        ) {
          void processFilesForMapping({
            questionnaireFile,
            sourceFiles,
            name,
            navigate,
          });
          return;
        }
        addNotification({
          message:
            typeof response?.message === "string" &&
              response?.message.length > 0
              ? response?.message
              : "Error in getting the structure of the questionnaire",
          type: "error",
        });
        navigate?.(`/${ROUTES.AGENT}`);
      }
    },
  );

  navigate?.(`/${ROUTES.AGENT}/${session.id}`);
};

const createColumnMapping = (columnLetters: string[]) => {
  return columnLetters.reduce((acc, col) => {
    if (!col || col === dataNotFound) return acc;
    acc.push({
      columnLetter: col,
      columnIndex: getColumnIndex(col),
    });
    return acc;
  }, [] as ColumnMappingData[]);
};

export const applyMappingFromAI = async (
  agentId: string,
  data: QuestionnaireStructure[],
) => {
  return new Promise<QuestionnaireMapping>((resolve) => {
    const finalData: QuestionnaireMapping = {};
    const { setSheetMappingData } = getAgentStateActions();
    const agentData = getAgentData<
      AGENT_TYPES.QUESTIONNAIRE,
      QuestionnaireTypes.QA
    >(agentId);
    if (!agentData) return;
    data.forEach(({ response, sheet_number }) => {
      const sheetName =
        agentData.mainData.questionnaireFileData.sheetNames[sheet_number];
        if(sheetName){
          if (Object.keys(response).length === 0) {
            finalData[sheetName] = {
              questionStartRow: null,
              questions: [],
              comments: [],
              answers: [],
              questionType: [],
            };
          } else {
            const questions =
              response.questions && typeof response.questions === "object"
                ? createColumnMapping(response.questions.columnLetters)
                : [];
    
            const comments =
              response.comments && typeof response.comments === "object"
                ? createColumnMapping(response.comments.columnLetters)
                : [];
    
            const answers =
              response.answers && typeof response.answers === "object"
                ? createColumnMapping(response.answers.columnLetters)
                : [];
    
            const questionType =
              response.answerType && typeof response.answerType === "object"
                ? createColumnMapping(response.answerType.columnLetters)
                : [];
    
            finalData[sheetName] = {
              questionStartRow:
                typeof response.questionStartRow === "number"
                  ? response.questionStartRow
                  : null,
              questions,
              comments,
              answers,
              questionType,
            };
          }
          setSheetMappingData(agentId, {
            sheetName,
            mappingData: finalData[sheetName],
          });
        }
    });

    resolve(finalData);
  });
};

export const generateResponseData = async ({
  agentId,
}: {
  agentId: string;
}) => {
  const agentData = getAgentData<
    AGENT_TYPES.QUESTIONNAIRE,
    QuestionnaireTypes.QA
  >(agentId);
  const mapping = agentData?.mainData.questionnaireMapping;
  const stepsData = agentData?.stepData;
  const questionnaireFile = agentData?.mainData.questionnaireFileData;
  const sheetNames = questionnaireFile?.sheetNames;
  const agent_session_id = agentData?.sessionData.id;
  const knowledgeBaseFiles = agentData?.mainData.sourceFilesUrls;
  console.log("agentData",agentData)
  if (
    !mapping ||
    !stepsData ||
    !questionnaireFile ||
    !sheetNames ||
    !agent_session_id ||
    !knowledgeBaseFiles
  )
    return;

  const fillerData = Object.entries(mapping).reduce(
    (acc, [sheetName, sheetData]) => {
      const sheetIndex = sheetNames.indexOf(sheetName);
      if (sheetIndex === -1 || sheetData.status === SheetStatus.IGNORED)
        return acc;

      const answer_col_indexes= sheetData.answers.map((a) => a.columnIndex);
      
      let answer_values: string[] = []
      if(answer_col_indexes.length>1){
        sheetData.answers.forEach((a) => {
          const values = questionnaireFile.sheetData[sheetName].data[0][a.columnIndex]
          if(values){
            answer_values.push(values)
          }
        })
      }

      const sheetFiller: QuestionnaireFiller = {
        sheet_name: sheetName,
        sheet_index: sheetIndex,
        metadata: {
          questions_start_row:
            typeof sheetData.questionStartRow === "number"
              ? sheetData.questionStartRow
              : null,
          question_col_index: sheetData.questions[0].columnIndex,
          question_type_column:
            sheetData.questionType.length > 0
              ? sheetData.questionType[0].columnIndex
              : null,
          comment_col_index:
            sheetData.comments.length > 0
              ? sheetData.comments[0].columnIndex
              : null,
          answer_col_indexes,
          answer_values: answer_values.length ? answer_values : null,
        },
      };
      acc.push(sheetFiller);
      return acc;
    },
    [] as QuestionnaireFiller[],
  );

  const { setQuestionnaireFiller, updateAgentStepData } =
    getAgentStateActions();

  setQuestionnaireFiller(agentId, fillerData);
  let manualMappingStepData: AgentSessionStep | undefined = undefined;
  const updatedStepData = stepsData.reduce((acc, step) => {
    if (step.type === AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING) {
      step.status = AgentSessionStatus.COMPLETE;
      step.data = {
        input: {
          ...step.data?.input,
        },
        output: {
          required_document_type: "json",
          is_light: useUserStateSelector.getState().aiResponseType === AiResponseType.LITE,
          ...(knowledgeBaseFiles && knowledgeBaseFiles.size > 1
            ? {
              multiple_source_urls: Array.from(
                knowledgeBaseFiles.values(),
              ).map((f) => f.url),
            }
            : {
              kb_url: knowledgeBaseFiles?.values()?.next()?.value?.url || "",
            }),
          excel_url: questionnaireFile.url,
          excel_meta_data: fillerData,
        },
      };
      manualMappingStepData = step;
    }

    if (
      [
        AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING,
        AgentSessionStepType.EXTRACT_QUESTIONS,
      ].includes(step.type)
    ) {
      step.status = AgentSessionStatus.COMPLETE;
    }
    if (step.type === AgentSessionStepType.QA_FILLER) {
      step.status = AgentSessionStatus.IN_PROGRESS;
    }
    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

  if (manualMappingStepData) {
    // await updateAgentSessionStep(manualMappingStepData);
  }
  updateAgentStepData(agent_session_id, updatedStepData);

  const QAFillerStep = updatedStepData.find(
    (step): step is AgentSessionStep =>
      (step.type as AgentSessionStepType) === AgentSessionStepType.QA_FILLER,
  );


  const agent_session_step_id = QAFillerStep?.id ?? ""

  let sessionsResponseQuality: AiResponseType 
  if(agentData.sessionData.qa_filler.response_quality){
    sessionsResponseQuality = agentData.sessionData.qa_filler.response_quality
  } else {
    sessionsResponseQuality =  userStateSelector.getState().aiResponseType === AiResponseType.NONE ? AiResponseType.LITE : userStateSelector.getState().aiResponseType
  }
  const { response_mode, response_quality } = getResponseTypes(sessionsResponseQuality);
  
  
  let source_urls: string[] = []

  if(agentData.sessionData.qa_filler.multiple_source_urls){
    source_urls = agentData.sessionData.qa_filler.multiple_source_urls
  }
  if(agentData.sessionData.qa_filler.kb_url){
    source_urls = [agentData.sessionData.qa_filler.kb_url]
  }
  
  
  
  const questionnaireAssessmentRequest: QARagCreateRequest = {
    agent_session_id,
    agent_session_step_id,
    response_mode,
    task: 'qa_filler',
    
    rag_input: {
      response_quality,
      task: 'qa_filler',
      doc_type: "json",
      source_urls:  agentData.sessionData.qa_filler.source_urls,
      excel_meta_data: fillerData,
      excel_url:  agentData.sessionData.qa_filler.excel_url,

    }
  }
  
  emitRagCreate(questionnaireAssessmentRequest)

};

export function transformAnswerData(data: {
  answer: string;
  answer_col_indexes: number[];
  answer_values: string[];
  answer_type: string;
}) {
  const isFreetext = (!data.answer_values?.length)

  return [{
    key: 'answer',
    title: "Answer",
    shouldRender: true,
    type: isFreetext ? ReviewResponseType.TEXT : ReviewResponseType.SINGLE_SELECT,
    options : data.answer_values,
    value: data.answer,
  }]
}

export const  transformQuestionsJsonToReviewData = (selectedControl: any) => {
  const keysToRender: string[] = [
    "sources",
    "additional_information",
  ];

  const answerData = transformAnswerData(selectedControl)

      const {sources, answer, ...restQuestions} = selectedControl;

      const RestQAFields = Object.entries({...restQuestions, sources}).reduce(
        (acc, [key, value]) => {
          const data: ReviewResponseData<
            keyof AgentSpecificReviewResponseType<
              AGENT_TYPES.QUESTIONNAIRE,
              QuestionnaireTypes.QA
            >
          > = {
            type: getRenderType(key),
            value:value,
            key: key as keyof AgentSpecificReviewResponseType<
              AGENT_TYPES.QUESTIONNAIRE,
              QuestionnaireTypes.QA
            >,
            title: getFieldTitle(key),
            shouldRender: keysToRender.includes(key),
          };
          acc.push(data);
          return acc;
        },
        [] as ReviewResponseData<
          keyof AgentSpecificReviewResponseType<
            AGENT_TYPES.QUESTIONNAIRE,
            QuestionnaireTypes.QA
          >
        >[],
      )

      return  [...answerData, ...RestQAFields]
}




export const processFileForQuestionaire = async (url: string, sessionId: string) => {

  const sessionControls = await getAgentSessionControls(sessionId);
  
  const questionsJson = sessionControls.data?.map((data: any) => ({...data.qa_filler, qa_filler_id: data.qa_filler.id})) || [];
  const session = await getAgentSession(sessionId);

  const ids: string[] = [];
  
  const tableRows: QuestionnaireTableRow[] = [];
  const approvedIds: string[] = [];
  const editedIds: string[] = [];
  const reviewResponse = new Map<
    string,
    ReviewResponseData<
      keyof AgentSpecificReviewResponseType<
        AGENT_TYPES.QUESTIONNAIRE,
        QuestionnaireTypes.QA
      >
    >[]
  >(
    questionsJson.flat().map((question: any) => {
      const id = question.id;
      ids.push(id);
      const tableRow: QuestionnaireTableRow = {
        id,
        question: question.question,
        confidence: question.confidence,
        sources: question.sources,
        status: question.approved
          ? "approved"
          : question.edited
            ? "edited"
            : "none",
      };
      tableRows.push(tableRow);
      if (question.approved) {
        approvedIds.push(id);
      }
      if (question.edited) {
        editedIds.push(id);
      }

      const restFields = transformQuestionsJsonToReviewData(question)

      return [ id, restFields];
    }),
  );

  const currentData = getAgentData<
    AGENT_TYPES.QUESTIONNAIRE,
    QuestionnaireTypes.QA
  >(sessionId);
  if (!currentData) {
    throw new Error("An error occurred");
  }

  const stepData = currentData.stepData.map((step) => {
    if (
      [
        AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING,
        AgentSessionStepType.EXTRACT_QUESTIONS,
        AgentSessionStepType.QA_FILLER,
      ].includes(step.type)
    ) {
      step.status = AgentSessionStatus.COMPLETE;
    }
    if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
      step.status = AgentSessionStatus.INPUT_NEEDED;
    }
    return step;
  });

  const agentData: Partial<
    AgentData<AGENT_TYPES.QUESTIONNAIRE, QuestionnaireTypes.QA>
  > = {
    agentType: AGENT_TYPES.QUESTIONNAIRE,
    sessionData: session.data.session as QaAgentSession,
    stepData,
    mainData: {
      ...currentData.mainData,
      reviewResponseData: reviewResponse,
      reviewResponseIds: ids,
      tableRows,
      searchFor: ["question"],
      searchTerm: "",
      approvedIds,
      editedIds,
    },
  };

  const { updateAgentData } = getAgentStateActions();

  updateAgentData<AGENT_TYPES.QUESTIONNAIRE, QuestionnaireTypes.QA>(
    sessionId,
    agentData,
  );
};

interface ProcessManualMappingArgs {
  agent_session_id: string;
  structureData?: QuestionnaireStructure[];
  shouldNavigate?: boolean;
  navigate?: NavigateFunction;
}

export const processManualMapping = async ({
  agent_session_id,
  structureData,
  shouldNavigate,
  navigate,
}: ProcessManualMappingArgs) => {
  const { data: sessionData } = await getAgentSession(agent_session_id ?? "");
  const { updateAgentStepData } = getAgentStateActions();
  let data: QuestionnaireMapping | undefined;
  let currentSessionStep: AgentSessionStep | undefined = undefined;

  if (structureData) {
    data = await applyMappingFromAI(agent_session_id, structureData);
  } else {
    const qaStructureStep = sessionData.steps.find(
      (step) => step.type === AgentSessionStepType.QA_EXCEL_STRUCTURE_MAPPING,
    );
    if (
      !qaStructureStep ||
      qaStructureStep.status !== AgentSessionStatus.COMPLETE
    )
      return;
    data = await applyMappingFromAI(
      agent_session_id,
      qaStructureStep.data.output.structure,
    );
  }

  
  const updatedSteps = sessionData.steps.reduce((acc, step) => {
    if (step.type === AgentSessionStepType.QA_EXCEL_STRUCTURE_MAPPING) {
      step.status = AgentSessionStatus.COMPLETE;
    }

    if (step.type === AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING) {
      currentSessionStep = {
        ...step,
        data: {
          ...step.data,
          input: data,
        },
        status: AgentSessionStatus.INPUT_NEEDED,
      };
      acc.push(currentSessionStep);
      return acc;
    }
    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);
  
  // if (currentSessionStep) await updateAgentSessionStep(currentSessionStep);
  updateAgentStepData(agent_session_id, updatedSteps);
  if (shouldNavigate && navigate)
    navigate({
      pathname: `/${ROUTES.AGENT}/${agent_session_id}`,
    });
};
interface FinalQuestionReviewResponse extends QuestionReviewReponse {
  approved: boolean;
  edited: boolean;
  id: string;
  qa_filler_id?: string;
}

export const generateFinalQAJSON = (agentId: string) => {
  const agentData = getAgentData<
    AGENT_TYPES.QUESTIONNAIRE,
    QuestionnaireTypes.QA
  >(agentId);
  if (!agentData) {
    throw new Error("An error occurred");
  }
  const { mainData } = agentData;
  const { reviewResponseData } = mainData;
  if (!reviewResponseData) {
    throw new Error("An error occurred");
  }

  const approvedIds = mainData.approvedIds;
  const editedIds = mainData.editedIds;
  const finalQuestionJson: FinalQuestionReviewResponse[] = [];
  reviewResponseData.forEach((item, id) => {

    const controlFields = {} as FinalQuestionReviewResponse;

    item.forEach((field) => { 
        controlFields[field.key] = field.value;
    })

    const question: FinalQuestionReviewResponse = {
      ...controlFields,
      approved: approvedIds.includes(id),
      edited: editedIds.includes(id),
      id,
    };
    finalQuestionJson.push(question);
  });
  return finalQuestionJson;
};

export const handleQuestionnaireAutoSave = async ({
  agentId,
  markAsComplete,
}: AutoSaveFunctionArgs) => {
  const questionsData = generateFinalQAJSON(agentId);
  const questions = JSON.stringify(questionsData);
  const blob = new Blob([questions], { type: "application/json" });
  const agentData = getAgentData<
    AGENT_TYPES.QUESTIONNAIRE,
    QuestionnaireTypes.QA
  >(agentId);
  if (!agentData) {
    throw new Error("An error occurred");
  }
  const { stepData } = agentData;
  const { staleUrl, selectedId } = agentData.mainData;

  const { setStaleUrl, updateAgentStepData } = getAgentStateActions();
  const editStepData = stepData?.find(
    (step) => step.type === AgentSessionStepType.EDIT_RESPONSE,
  );

  if (!editStepData) {
    throw new Error("An occurred while saving");
  }

  let markAsCompleteDone = false;

  const stepUrl = editStepData?.data?.url;
  let currentStaleUrl = staleUrl || stepUrl;

  if (!currentStaleUrl) {
    const signedUrl = await getSignedUrl({
      file_names: ["updated_questions.json"],
      max_age: 86400,
    });
    const updatedStep = {
      ...editStepData,
      data: {
        url: signedUrl[0],
      },
      status: markAsComplete
        ? AgentSessionStatus.COMPLETE
        : AgentSessionStatus.INPUT_NEEDED,
    };
    const updatedSteps = stepData.map((step) => {
      if (step.id === updatedStep.id) {
        return updatedStep;
      }
      return step;
    });
    updateAgentStepData(agentId, updatedSteps);
    await updateAgentSessionStep(updatedStep);
    currentStaleUrl = signedUrl[0];
    markAsCompleteDone = true;
    setStaleUrl(agentId, currentStaleUrl);
  }
  const expiryDate = new URL(currentStaleUrl).searchParams.get("se");
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    const currentTime = new Date();
    const diff = expiry.getTime() - currentTime.getTime();
    if (diff < 0) {
      const signedUrl = await getSignedUrl({
        stale_urls: [currentStaleUrl],
        max_age: 86400,
      });
      currentStaleUrl = signedUrl[0];
      setStaleUrl(agentId, currentStaleUrl);
    }
  }
  await uploadFileReq(currentStaleUrl, blob);

  const control = questionsData.find((item) => item.id === selectedId);
  if (!control) return;

  const { emitControlEdit } = useAgentSocket();
  try {
    const agentData = getAgentData<AGENT_TYPES.QUESTIONNAIRE, any>(agentId);
    const editedField = agentData?.mainData.lastEditedField;

    if(control.qa_filler_id){
      control.id = control.qa_filler_id
    }

    await emitControlEdit(
      currentStaleUrl,
      control,
      control.id,
      agentId,
      editStepData.id,
      editedField ?? ''
    );
  } catch (error) {
    console.error('Error in emitControlEdit:', error);
    throw error;
  }
};

export const getQAJsonFromAPI = async (
  agentId: string,
) => {
  try {
    const agentData = getAgentData<
      AGENT_TYPES.QUESTIONNAIRE,
      QuestionnaireTypes.QA
    >(agentId);
    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { mainData, sessionData, stepData, responseQuality } = agentData;
    const excel_metadata = mainData.questionnaireFiller;
    const questionnaireFile = mainData.questionnaireFileData;
    const workflowName = sessionData.name;
    const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();

    addNotification({
      type: "success",
      title: "Report Download in Progress...",
      message: "If the download does not begin within 15 seconds, please click the ‘Download’ button",
    });

    const data = await getRAGExcelFromJSON({
      task: "qa_filler",
      output_format: "excel",
      response_quality: responseQuality ?? AiResponseType.LITE,
      agent_session_id: agentId,
      use_for_ai_training: false,
    });

    setFinalFileUrl(agentId, data.message);
    // Direct download using the file URL
    fetch(data.data.file_url)
    .then(response => response.blob())
    .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = getFileNameForDownloadReport(sessionData);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl); // Cleanup
    })


   

    const downloadStepData = stepData?.find(
      (step) => step.type === AgentSessionStepType.PREPARE_REPORT,
    );
    const editResponseStep = stepData?.find(
      (step) => step.type === AgentSessionStepType.EDIT_RESPONSE,
    );
    const updatedStepData = stepData.map((step) => {
      if (step.id === editResponseStep?.id) {
        return { ...step, status: AgentSessionStatus.COMPLETE };
      }
      return step;
    });

    if (downloadStepData?.id) {
      await updateAgentSessionStep({
        ...downloadStepData,
        data: {
          url: data.message,
        },
      });
    }

    updateAgentStepData(agentId, updatedStepData);
  } catch (e) {
    console.error(e);
    addNotification({
      message: "Error in downloading the file",
      type: "error",
    });
  }
};

export const handleEndQuestionnaireSession = async (
  sessionId: string,
  navigate: NavigateFunction,
) => {
  const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();
  try {
    const agentData = getAgentData<
      AGENT_TYPES.QUESTIONNAIRE,
      QuestionnaireTypes.QA
    >(sessionId);
    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { stepData } = agentData;

    const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

    //if user directly clicks on end session without download report
    if (editResponseStep?.status !== AgentSessionStatus.COMPLETE) {
      const fairData = generateFinalQAJSON(sessionId);
      const blob = new Blob([JSON.stringify(fairData)], { type: "application/json" });

      const signedUrl = await getSignedUrl({
        file_names: ["updated_fair.json"],
        max_age: 86400,
      });

      // Process the JSON file first
      await processFileForQuestionaire(signedUrl[0], sessionId);

      const data = await getRAGExcelFromJSON({
        task: "qa_filler",
        output_format: "excel",
        response_quality: agentData.responseQuality ?? AiResponseType.LITE,
        agent_session_id: sessionId,
         use_for_ai_training: false,
      });

      setFinalFileUrl(sessionId, data.data.file_url);

      const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

      const updatedStepData = stepData.map((step) => {
        if (step.id === editResponseStep?.id) {
          return {
            ...step,
            status: AgentSessionStatus.COMPLETE,
            data: {
              url: signedUrl[0], //  JSON URL for future processing
            },
          };
        }
        if (step.id === downloadStepData?.id) {
          return {
            ...step,
            data: {
              url: data.data.file_url,
            },
            status: AgentSessionStatus.COMPLETE,
          };
        }
        return step;
      });

      if (downloadStepData?.id) {
        await updateAgentSessionStep({
          ...downloadStepData,
          data: {
            url: data.data.file_url,
          },
          status: AgentSessionStatus.COMPLETE,
        });
      }

      updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    } else {
    const prepareReportStep = stepData?.find(
      (step) => step.type === AgentSessionStepType.PREPARE_REPORT,
    );
    if (prepareReportStep?.id) {
      await updateAgentSessionStep({
        ...prepareReportStep,
        status: AgentSessionStatus.COMPLETE,
      });
    }
    const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
    navigate(successPath);
  }
  } catch (error) {
    console.error(error);
    addNotification({
      message: "Error in ending the session",
      title: "Error",
    });
  }

};
